import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StitchServiceFactory } from '@modules/common/factories/stitch-service.factory';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { KnowledgePanelService } from '@modules/knowledge/services/knowledge-panel.service';
import {
  KP_RESULT_TYPES,
  type KPResultCounts,
  type KPResultListType,
  type KPResultType,
} from '@modules/knowledge/types/kp-result-types';
import {
  buildKPListRequest,
  kpListSearchConfig,
  type KPResultsQuery,
} from '@modules/knowledge/utils/build-kp-results-filters';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { ViewSubjects } from '@sharedlib/common/permissions/types/Permissions';
import { BehaviorSubject, combineLatest, EMPTY, merge, type Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';

/** A count needs the total alone, so each request asks for the smallest page. */
const COUNT_PAGE = { limit: 1, offset: 0 } as const;

/**
 * The signals the Knowledge Hub refreshes its counts on. Pin and flag are left out: they cannot change a
 * count taken with default filters, and the selected type's count is its list's.
 */
const REFRESH_EVENTS = [
  SignalStitchEventType.ARCHIVED,
  SignalStitchEventType.UNARCHIVED,
  SignalStitchEventType.MOVED_TO_TRASH,
  SignalStitchEventType.RESTORED_FROM_TRASH,
];

/** Collapses a bulk operation's burst of signals into one refresh, as the Knowledge Hub does. */
const REFRESH_DEBOUNCE_MS = 300;

interface KeyedQuery {
  readonly key: string;
  readonly query: KPResultsQuery;
}

interface TypeCount {
  readonly key: string;
  readonly type: KPResultListType;
  /** `undefined` when the request failed. */
  readonly count: number | undefined;
}

/**
 * The result counts the Knowledge Panel's type menu shows for the types that are not selected; the
 * selected type's count is its own list's. Each type is counted as its list first shows: with the KP's
 * markers and search, and its sort menu untouched. Counts are fetched only while the menu is open.
 */
@Injectable()
export class KpResultCountsService {
  private readonly kpService = inject(KnowledgePanelService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  private readonly stitchServiceFactory = inject(StitchServiceFactory);
  private readonly stitchEventsService = inject(StitchEventsService);
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionsService = inject(PermissionsService);

  private availableTypes(): readonly KPResultType[] {
    return KP_RESULT_TYPES.filter(
      (entry) =>
        (entry.stitchType !== StitchType.SHEET && entry.stitchType !== StitchType.SHEET_FOLDER) ||
        this.permissionsService.checkPermission('read', ViewSubjects.sheetsView),
    );
  }

  private readonly countsState = signal<KPResultCounts>({});
  /** Per type; a type with no number yet reads as 0. */
  public readonly counts = this.countsState.asReadonly();

  /** The type selected while the menu is open; `null` while it is closed. */
  private readonly openedFor = new BehaviorSubject<KPResultListType | null>(null);
  /** Identifies the query the counts on screen came from. */
  private countsKey = '';

  constructor() {
    this.openedFor
      .pipe(
        switchMap((selected) => (selected ? this.countWhileOpen(selected) : EMPTY)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ key, type, count }) => {
        this.applyCount(key, type, count);
      });
  }

  /** Starts counting every type but `selected`, and keeps the counts live until {@link close}. */
  public open(selected: KPResultListType): void {
    this.openedFor.next(selected);
  }

  public close(): void {
    if (this.openedFor.value !== null) {
      this.openedFor.next(null);
    }
  }

  private countWhileOpen(selected: KPResultListType): Observable<TypeCount> {
    const counted = this.availableTypes().filter((entry) => entry.type !== selected);

    return combineLatest([this.queries(), this.refreshes().pipe(startWith(undefined))]).pipe(
      switchMap(([{ key, query }]) => {
        this.clearCountsForNewQuery(key);
        return merge(
          ...counted.map((entry) =>
            this.countOf(entry, query).pipe(map((count) => ({ key, type: entry.type, count }))),
          ),
        );
      }),
    );
  }

  private queries(): Observable<KeyedQuery> {
    return combineLatest([
      this.kpService.getQueryFilter(),
      this.kpService.getContextStitchItem(),
      this.kpService.getKeywords(),
    ]).pipe(
      // Collapses the synchronous emissions of one change (and of the subscription itself) into one query.
      debounceTime(0),
      map(([markers, contextItem, keywords]) => {
        const query: KPResultsQuery = {
          tags: markers.tags,
          knots: markers.knots,
          connections: markers.connections,
          search: null,
          keywords,
          contextItem,
          timeZone: this.displayTimeZoneService.timeZone,
        };
        return { key: this.buildCountsKey(query), query };
      }),
      distinctUntilChanged((previous, current) => previous.key === current.key),
    );
  }

  /** Read at open: the menu cannot stay open across a space switch. */
  private refreshes(): Observable<unknown> {
    const contextId = this.spaceContextStore.contextId();
    return this.kpService.getContextStitchItem().pipe(
      switchMap((item) => merge(
        ...this.availableTypes().map((entry) => this.stitchEventsService.getEvents(
          (entry.stitchType === StitchType.CONTACT || entry.stitchType === StitchType.GROUP || entry.stitchType === StitchType.TASK || entry.stitchType === StitchType.PROJECT) && item?.accountId
            ? [...new Set([contextId, item.accountId])]
            : contextId,
          entry.stitchType,
          REFRESH_EVENTS,
        )),
      )),
      debounceTime(REFRESH_DEBOUNCE_MS),
    );
  }

  private countOf(entry: KPResultType, query: KPResultsQuery): Observable<number | undefined> {
    const request = buildKPListRequest(entry.type, query, COUNT_PAGE);
    if (request.withEmpty) {
      return of(0);
    }

    // The tenant its list searches, so the badge is the number that list pages through.
    return this.stitchServiceFactory
      .getServiceByStitchType(entry.stitchType)
      .count(request, kpListSearchConfig(entry.type, query.contextItem))
      .pipe(catchError(() => of(undefined)));
  }

  /** Everything that changes what the requests ask for. */
  private buildCountsKey(query: KPResultsQuery): string {
    const names = (markers: ReadonlyArray<{ name?: string }> | undefined): string[] =>
      (markers ?? []).map((marker) => marker.name);
    const contextItem = query.contextItem;

    return JSON.stringify([
      names(query.tags),
      names(query.knots),
      names(query.connections),
      query.keywords ?? [],
      query.timeZone(),
      contextItem ? [contextItem.getStitchType(), contextItem.id ?? null, contextItem.accountId ?? null] : null,
    ]);
  }

  /**
   * A changed query starts from unknown, which reads as 0, rather than showing the previous query's numbers
   * under it. A refresh or a reopen of the same query keeps its numbers until the new ones land.
   */
  private clearCountsForNewQuery(key: string): void {
    if (key !== this.countsKey) {
      this.countsState.set({});
      this.countsKey = key;
    }
  }

  /** A failed request keeps the number on screen, which {@link clearCountsForNewQuery} kept only for the same query. */
  private applyCount(key: string, type: KPResultListType, count: number | undefined): void {
    if (key !== this.countsKey || count === undefined) {
      return;
    }
    this.countsState.update((counts) => ({ ...counts, [type]: count }));
  }
}
